import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TablePagination,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Stars as StarsIcon,
  TrendingUp as TrendingUpIcon,
  FileDownload as FileDownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import axios from 'axios';
import * as XLSX from 'xlsx';

interface CustomerLoyalty {
  _id: string;
  name: string;
  phone: string;
  email: string;
  category: string;
  loyaltyBalance: number;
  pointsValue: number;
  totalEarned: number;
  totalRedeemed: number;
  lastTransaction?: string;
}

interface Statistics {
  totalCustomers: number;
  totalPoints: number;
  totalValue: number;
  activeCustomers: number;
}

const AllCustomersLoyaltyPoints: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerLoyalty[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    totalCustomers: 0,
    totalPoints: 0,
    totalValue: 0,
    activeCustomers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // جلب البيانات من API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage === -1 ? 999999 : rowsPerPage,
        search: searchTerm || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined
      };

      const response = await axios.get('/api/customers/all-customers', { params });

      if (response.data.success) {
        setCustomers(response.data.data.customers);
        setStatistics(response.data.data.statistics);
        setTotalCount(response.data.data.pagination.total);
      }
    } catch (err: any) {
      console.error('Error fetching customers loyalty points:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, categoryFilter]);

  // البحث عند الضغط على Enter
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchData();
    }
  };

  const handleSearchClick = () => {
    setPage(0);
    fetchData();
  };

  const handleReset = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setPage(0);
    fetchData();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRowsPerPage(value === 'all' ? -1 : parseInt(value, 10));
    setPage(0);
  };

  // دالة الترتيب
  const handleSort = (field: string) => {
    if (sortField === field) {
      // إذا كان نفس الحقل، عكس الترتيب
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // حقل جديد، ابدأ بالترتيب التنازلي
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // ترتيب البيانات
  const sortedCustomers = React.useMemo(() => {
    if (!sortField) return customers;

    return [...customers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'loyaltyBalance':
          aValue = a.loyaltyBalance;
          bValue = b.loyaltyBalance;
          break;
        case 'pointsValue':
          aValue = a.pointsValue;
          bValue = b.pointsValue;
          break;
        case 'totalEarned':
          aValue = a.totalEarned;
          bValue = b.totalEarned;
          break;
        case 'totalRedeemed':
          aValue = a.totalRedeemed;
          bValue = b.totalRedeemed;
          break;
        case 'lastTransaction':
          aValue = a.lastTransaction ? new Date(a.lastTransaction).getTime() : 0;
          bValue = b.lastTransaction ? new Date(b.lastTransaction).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [customers, sortField, sortOrder]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ar });
  };

  // دالة تصدير البيانات إلى Excel
  const handleExportToExcel = () => {
    // تحضير البيانات للتصدير (استخدام البيانات المرتبة)
    const exportData = sortedCustomers.map((customer, index) => ({
      '#': index + 1,
      'اسم العميل': customer.name,
      'الهاتف': customer.phone || '-',
      'البريد الإلكتروني': customer.email || '-',
      'الفئة': getCategoryLabel(customer.category),
      'رصيد النقاط': customer.loyaltyBalance,
      'قيمة النقاط (ريال)': customer.pointsValue.toFixed(2),
      'إجمالي المكتسب': customer.totalEarned,
      'إجمالي المستبدل': customer.totalRedeemed,
      'آخر حركة': formatDate(customer.lastTransaction)
    }));

    // إنشاء ورقة عمل
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // تعيين عرض الأعمدة
    worksheet['!cols'] = [
      { wch: 5 },  // #
      { wch: 25 }, // اسم العميل
      { wch: 15 }, // الهاتف
      { wch: 25 }, // البريد
      { wch: 10 }, // الفئة
      { wch: 12 }, // رصيد النقاط
      { wch: 15 }, // قيمة النقاط
      { wch: 15 }, // إجمالي المكتسب
      { wch: 15 }, // إجمالي المستبدل
      { wch: 12 }  // آخر حركة
    ];

    // إنشاء كتاب عمل
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'نقاط الولاء');

    // تصدير الملف
    const fileName = `نقاط_الولاء_للعملاء_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'regular':
        return 'عادي';
      case 'premium':
        return 'مميز';
      case 'vip':
        return 'VIP';
      default:
        return category;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'regular':
        return 'default';
      case 'premium':
        return 'primary';
      case 'vip':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // مكون رأس العمود القابل للترتيب
  const SortableTableCell: React.FC<{
    field: string;
    label: string;
    align?: 'left' | 'center' | 'right';
  }> = ({ field, label, align = 'left' }) => (
    <TableCell
      align={align}
      sx={{
        fontWeight: 'bold',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
      onClick={() => handleSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
        {label}
        {sortField === field && (
          sortOrder === 'asc' ? (
            <ArrowUpwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
          ) : (
            <ArrowDownwardIcon fontSize="small" sx={{ color: 'primary.main' }} />
          )
        )}
      </Box>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <StarsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          نقاط الولاء للعملاء
        </Typography>
      </Box>

      {/* بطاقات الإحصائيات */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    إجمالي العملاء
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalCustomers}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    عميل
                  </Typography>
                </Box>
                <StarsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    إجمالي النقاط
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalPoints.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    نقطة
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    القيمة الإجمالية
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.totalValue.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    ريال سعودي
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    العملاء النشطون
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {loading ? '...' : statistics.activeCustomers}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    عميل نشط
                  </Typography>
                </Box>
                <StarsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* الفلاتر */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              تصفية النتائج
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={loading || customers.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                }
              }}
            >
              تصدير إلى Excel
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="بحث"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                placeholder="اسم العميل، الهاتف، البريد..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>فئة العميل</InputLabel>
                <Select
                  value={categoryFilter}
                  label="فئة العميل"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="regular">عادي</MenuItem>
                  <MenuItem value="premium">مميز</MenuItem>
                  <MenuItem value="vip">VIP</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearchClick}
                sx={{ height: '40px' }}
              >
                بحث
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ height: '40px' }}
              >
                إعادة تعيين
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* الجدول */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            قائمة العملاء ونقاط الولاء
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : customers.length === 0 ? (
            <Alert severity="info">
              لا توجد بيانات لعرضها
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>اسم العميل</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>الهاتف</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>البريد الإلكتروني</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">الفئة</TableCell>
                    <SortableTableCell field="loyaltyBalance" label="رصيد النقاط" align="center" />
                    <SortableTableCell field="pointsValue" label="قيمة النقاط (ريال)" align="center" />
                    <SortableTableCell field="totalEarned" label="إجمالي المكتسب" align="center" />
                    <SortableTableCell field="totalRedeemed" label="إجمالي المستبدل" align="center" />
                    <SortableTableCell field="lastTransaction" label="آخر حركة" align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCustomers.map((customer) => (
                      <TableRow key={customer._id} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {customer.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{customer.phone || '-'}</TableCell>
                        <TableCell>{customer.email || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getCategoryLabel(customer.category)}
                            color={getCategoryColor(customer.category) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              color: customer.loyaltyBalance > 0 ? 'success.main' : 'text.secondary',
                              fontSize: '1.1rem'
                            }}
                          >
                            {customer.loyaltyBalance.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              color: 'primary.main'
                            }}
                          >
                            {customer.pointsValue.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ color: 'success.main' }}>
                            {customer.totalEarned.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ color: 'warning.main' }}>
                            {customer.totalRedeemed.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {formatDate(customer.lastTransaction)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, { label: 'الكل', value: -1 }]}
                labelRowsPerPage="عدد الصفوف:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
                }
                sx={{
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    marginBottom: 0,
                  },
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllCustomersLoyaltyPoints;
